/**
 * Custom Engineering Hydraulic Calculation Engine
 * Strict input validation, zero division guards, and verified commercial pipe lookup.
 */

import { getWaterProperties } from "./fluids.js";
import { getVerifiedPipes, MATERIALS } from "./pipeDatabase.js";





export function validateSizingInputs(inputs) {
  if (typeof inputs.flowRateM3s !== "number" || isNaN(inputs.flowRateM3s) || inputs.flowRateM3s <= 0) {
    return { isValid: false, error: "Flow rate must be greater than zero." };
  }
  if (typeof inputs.targetVelocityMs !== "number" || isNaN(inputs.targetVelocityMs) || inputs.targetVelocityMs <= 0) {
    return { isValid: false, error: "Target velocity must be greater than zero." };
  }
  if (typeof inputs.pipeLengthMeters !== "number" || isNaN(inputs.pipeLengthMeters) || inputs.pipeLengthMeters <= 0) {
    return { isValid: false, error: "Pipe length must be greater than zero." };
  }
  if (typeof inputs.temperatureC !== "number" || isNaN(inputs.temperatureC) || inputs.temperatureC < 0 || inputs.temperatureC > 100) {
    return { isValid: false, error: "Water operating temperature must be between 0°C and 100°C." };
  }
  if (!inputs.materialId || !MATERIALS[inputs.materialId]) {
    return { isValid: false, error: `Unsupported material identifier: "${inputs.materialId}".` };
  }
  return { isValid: true };
}

export function calculateRequiredDiameterMm(flowRateM3s, targetVelocityMs) {
  if (flowRateM3s <= 0 || targetVelocityMs <= 0) {
    throw new Error("Flow rate and target velocity must be positive non-zero numbers.");
  }
  const dMeters = Math.sqrt((4 * flowRateM3s) / (Math.PI * targetVelocityMs));
  return dMeters * 1000;
}

export function calculateVelocity(flowRateM3s, innerDiameterMm) {
  if (flowRateM3s <= 0 || innerDiameterMm <= 0) {
    throw new Error("Flow rate and inner diameter must be positive non-zero numbers.");
  }
  const dMeters = innerDiameterMm / 1000;
  const area = (Math.PI * Math.pow(dMeters, 2)) / 4;
  return flowRateM3s / area;
}

export function calculateReynoldsNumber(velocityMs, innerDiameterMm, kinematicViscosityM2s) {
  if (velocityMs <= 0 || innerDiameterMm <= 0 || kinematicViscosityM2s <= 0) {
    return 0;
  }
  const dMeters = innerDiameterMm / 1000;
  return (velocityMs * dMeters) / kinematicViscosityM2s;
}

export function calculateDarcyFrictionFactor(reynolds, roughnessMm, innerDiameterMm) {
  if (reynolds <= 0 || innerDiameterMm <= 0) return 0.02;

  // Laminar flow: f = 64 / Re
  if (reynolds <= 2300) {
    return Math.min(0.1, 64 / reynolds);
  }

  const dM = innerDiameterMm / 1000;
  const epsM = Math.max(1e-6, (roughnessMm || 0.0015) / 1000);
  const relRoughness = epsM / dM;

  // Turbulent flow: Swamee-Jain explicit formulation
  if (reynolds >= 4000) {
    const term = (relRoughness / 3.7) + (5.74 / Math.pow(reynolds, 0.9));
    const log10Term = Math.log10(term);
    return 0.25 / Math.pow(log10Term, 2);
  }

  // Transitional zone (2300 < Re < 4000): Cubic smooth interpolation
  const fLaminar2300 = 64 / 2300; // ~0.0278
  const term4000 = (relRoughness / 3.7) + (5.74 / Math.pow(4000, 0.9));
  const fTurbulent4000 = 0.25 / Math.pow(Math.log10(term4000), 2);
  const alpha = (reynolds - 2300) / (4000 - 2300);
  return fLaminar2300 + alpha * (fTurbulent4000 - fLaminar2300);
}

export function calculateFullPipeSizing(inputs) {
  const validation = validateSizingInputs(inputs);
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  const { flowRateM3s, targetVelocityMs, materialId, pipeLengthMeters, temperatureC, fittingKTotal = 0 } = inputs;
  const g = 9.80665; // m/s^2

  const fluid = getWaterProperties(temperatureC);
  const material = MATERIALS[materialId];
  const pipes = getVerifiedPipes(materialId);

  const reqIDMm = calculateRequiredDiameterMm(flowRateM3s, targetVelocityMs);

  // Commercial pipe selection (ID sorted ascending)
  const sorted = [...pipes].sort((a, b) => a.innerDiameterMm - b.innerDiameterMm);
  let pipe = sorted[sorted.length - 1];

  for (const p of sorted) {
    const actualV = calculateVelocity(flowRateM3s, p.innerDiameterMm);
    if (p.innerDiameterMm >= reqIDMm * 0.98 || actualV <= targetVelocityMs * 1.05) {
      pipe = p;
      break;
    }
  }

  const actualV = calculateVelocity(flowRateM3s, pipe.innerDiameterMm);
  const Re = calculateReynoldsNumber(actualV, pipe.innerDiameterMm, fluid.kinematicViscosity);

  let flowRegime = "Turbulent";
  if (Re < 2300) flowRegime = "Laminar";
  else if (Re < 4000) flowRegime = "Transitional";

  const f = calculateDarcyFrictionFactor(Re, pipe.roughnessMm, pipe.innerDiameterMm);
  const relRoughness = (pipe.roughnessMm / 1000) / (pipe.innerDiameterMm / 1000);
  const dMeters = pipe.innerDiameterMm / 1000;

  // Darcy-Weisbach head loss
  const frictionHeadLossM = f * (pipeLengthMeters / dMeters) * (Math.pow(actualV, 2) / (2 * g));
  const minorHeadLossM = Math.max(0, fittingKTotal) * (Math.pow(actualV, 2) / (2 * g));
  const totalHeadLossM = frictionHeadLossM + minorHeadLossM;

  // Pressure drop
  const pressureDropPa = fluid.density * g * totalHeadLossM;
  const pressureDropKPa = pressureDropPa / 1000;
  const pressureDropBar = pressureDropPa / 100000;
  const pressureDropPsi = pressureDropPa * 0.000145038;

  const hydraulicGradientKPaPer100m = (pressureDropKPa / pipeLengthMeters) * 100;

  // Warnings
  const warnings = [];
  if (actualV > 2.5) {
    warnings.push(`High Flow Velocity (${actualV.toFixed(2)} m/s): Exceeds CIBSE / BS EN 806 recommended threshold (2.0–2.4 m/s). Risk of erosion-corrosion, acoustic noise and water hammer.`);
  } else if (actualV > 1.5 && temperatureC > 50) {
    warnings.push(`Domestic Hot Water Velocity (${actualV.toFixed(2)} m/s): Hot water systems above 50°C should not exceed 1.0–1.2 m/s to prevent accelerated pipe wall thinning.`);
  } else if (actualV < 0.5) {
    warnings.push(`Low Flow Velocity (${actualV.toFixed(2)} m/s): Velocity below 0.5 m/s can allow particulate settling, sedimentation, and biofilm/Legionella risk in water services.`);
  }

  if (hydraulicGradientKPaPer100m > 40) {
    warnings.push(`High Pressure Gradient (${hydraulicGradientKPaPer100m.toFixed(1)} kPa/100m): Normal building plumbing is designed for 10–30 kPa/100m to balance pump operating costs.`);
  }

  if (materialId === "pvc_sch40" && temperatureC > 45) {
    warnings.push(`Temperature Alert: PVC Schedule 40 is not rated for continuous hot water (>45°C). Use CPVC, Copper or PEX for hot water installations.`);
  }

  return {
    requiredDiameterMm: parseFloat(reqIDMm.toFixed(2)),
    recommendedPipe: pipe,
    actualVelocityMs: parseFloat(actualV.toFixed(3)),
    reynoldsNumber: Math.round(Re),
    flowRegime,
    frictionFactor: parseFloat(f.toFixed(4)),
    relativeRoughness: parseFloat(relRoughness.toFixed(6)),
    frictionHeadLossM: parseFloat(frictionHeadLossM.toFixed(3)),
    minorHeadLossM: parseFloat(minorHeadLossM.toFixed(3)),
    totalHeadLossM: parseFloat(totalHeadLossM.toFixed(3)),
    pressureDropKPa: parseFloat(pressureDropKPa.toFixed(2)),
    pressureDropBar: parseFloat(pressureDropBar.toFixed(4)),
    pressureDropPsi: parseFloat(pressureDropPsi.toFixed(2)),
    hydraulicGradientKPaPer100m: parseFloat(hydraulicGradientKPaPer100m.toFixed(2)),
    fluidProperties: fluid,
    warnings,
    calculationsExplanation: {
      continuityFormula: `D_{req} = \sqrt{\frac{4 \times ${(flowRateM3s * 1000).toFixed(2)} \text{ L/s}}{\pi \times ${targetVelocityMs.toFixed(2)} \text{ m/s}}} = ${reqIDMm.toFixed(2)} \text{ mm}`,
      velocityCheck: `v = \frac{4 \times ${flowRateM3s.toExponential(3)}}{\pi \times (${(dMeters).toFixed(4)})^2} = ${actualV.toFixed(2)} \text{ m/s}`,
      reynoldsFormula: `Re = \frac{${actualV.toFixed(2)} \times ${dMeters.toFixed(4)}}{${fluid.kinematicViscosity.toExponential(3)}} = ${Math.round(Re).toLocaleString()}`,
      frictionFormula: `f = \frac{0.25}{\left[ \log_{10}\left( \frac{${pipe.roughnessMm}\times 10^{-3}}{3.7 \times ${dMeters.toFixed(4)}} + \frac{5.74}{${Math.round(Re)}^{0.9}} \right) \right]^2} = ${f.toFixed(4)}`,
      darcyFormula: `\Delta P = ${f.toFixed(4)} \times \frac{${pipeLengthMeters}\text{m}}{${dMeters.toFixed(4)}\text{m}} \times \frac{${fluid.density.toFixed(1)} \times ${actualV.toFixed(2)}^2}{2} = ${pressureDropKPa.toFixed(1)} \text{ kPa}`,
    },
  };
}

/**
 * Reverse Darcy-Weisbach flow solver
 * Calculates flow rate from allowable pressure drop and pipe length
 */
export function calculateFlowFromPressureDrop(
  deltaPKPa,
  pipeLengthMeters,
  innerDiameterMm,
  roughnessMm = 0.0015,
  temperatureC = 20
) {
  if (deltaPKPa <= 0 || pipeLengthMeters <= 0 || innerDiameterMm <= 0) {
    throw new Error("Pressure drop, length, and diameter must be positive non-zero numbers.");
  }
  const fluid = getWaterProperties(temperatureC);
  const dM = innerDiameterMm / 1000;
  const g = 9.80665;
  const hf = (deltaPKPa * 1000) / (fluid.density * g);

  let f = 0.02;
  let v = 0;
  for (let i = 0; i < 6; i++) {
    v = Math.sqrt((hf * 2 * g * dM) / (f * pipeLengthMeters));
    const Re = (v * dM) / fluid.kinematicViscosity;
    f = calculateDarcyFrictionFactor(Re, roughnessMm, innerDiameterMm);
  }
  const area = (Math.PI * Math.pow(dM, 2)) / 4;
  return v * area; // m3/s
}
